"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseFieldTypeLogic = exports.percentFilled = exports.percentEmpty = exports.countFilled = exports.countEmpty = exports.emptyValuesAggregator = exports.valuesAggregator = void 0;
const core_1 = require("@sentry/core");
const at_1 = __importDefault(require("lodash-es/at"));
const isPlainObject_1 = __importDefault(require("lodash-es/isPlainObject"));
const keyBy_1 = __importDefault(require("lodash-es/keyBy"));
const omit_1 = __importDefault(require("lodash-es/omit"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const detailed_decimal_1 = require("../formatters/detailed-decimal");
const fields_1 = require("../utils/fields");
const types_1 = require("@smartsuite/types");
const number_1 = require("../utils/number");
const field_logic_logging_1 = require("./field-logic.logging");
const fields_logic_registry_1 = require("./fields-logic-registry");
const clipboard_1 = require("../utils/clipboard");
const google_sheet_1 = require("../utils/google-sheet");
const dom_1 = require("../utils/dom");
const intl_1 = require("../locale/intl");
const texts_1 = require("../texts");
const validation_1 = require("../validation/validation");
const view_control_rules_1 = require("../utils/view-control-rules");
const migrations_1 = require("../migrations/migrations");
const target_field_1 = require("../utils/target-field");
/**
 *
 * This whole aggregation thing below should be in a separate file,
 * but this creates a circular import and I couldn't find a simple
 * way of fixing it.
 *
 * The problem is that aggregator uses logic and registry,
 * but also base logic uses aggregators to define default set of aggregators.
 *
 * Types and actual aggregation function are already in separate files.
 *
 */
/**
 * Aggregator signature is quite high level - it receives list of records and field.
 * Many times we just want to write a simple function that just processes values.
 *
 * This function generates and aggregator function from a simple function that processes
 * values.
 */
function valuesAggregator(aggregate) {
    return (records = [], field, targetOfField, userPreferences) => {
        const logic = (0, fields_logic_registry_1.getFieldLogic)(field.field_type);
        if (targetOfField) {
            const targetOfFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(targetOfField.field_type);
            const targetOfFieldValues = records.map((record) => targetOfFieldLogic.getFieldValueFromRecord(record, targetOfField));
            return aggregate(targetOfFieldValues, logic, field, userPreferences);
        }
        const values = records.map((record) => logic.getFieldValueFromRecord(record, field));
        return aggregate(values, logic, field, userPreferences);
    };
}
exports.valuesAggregator = valuesAggregator;
/**
 * Creates aggregator that processes empty values.
 */
function emptyValuesAggregator(aggregate) {
    return (records = [], field, targetOfField, userPreferences) => {
        // For lookup field we need to calculate empties separately
        // because lookup value is array of data and valuesAggregator flat this data by default.
        if ((targetOfField === null || targetOfField === void 0 ? void 0 : targetOfField.field_type) === "lookupfield") {
            const targetOfFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(targetOfField.field_type);
            const empties = records.filter((record) => {
                // Get lookup values without empty data for target field. E.g.:
                // [[1, 15]], [[null]], [], [] -> 3 values is empty, 1 is filled
                const targetValue = targetOfFieldLogic.getFieldValueFromRecord(record, targetOfField);
                return targetOfFieldLogic.isValueEmpty(targetValue, targetOfField);
            });
            return aggregate(empties.length, records.length);
        }
        return valuesAggregator((values, logic) => {
            const empties = values.filter((value) => logic.isValueEmpty(value, field));
            return aggregate(empties.length, values.length);
        })(records, field, targetOfField, userPreferences);
    };
}
exports.emptyValuesAggregator = emptyValuesAggregator;
exports.countEmpty = emptyValuesAggregator((empty) => empty);
exports.countFilled = emptyValuesAggregator((empty, total) => total - empty);
exports.percentEmpty = emptyValuesAggregator((empty, total) => Number(((empty / total) * 100).toFixed(2)));
exports.percentFilled = emptyValuesAggregator((empty, total) => Number((((total - empty) / total) * 100).toFixed(2)));
class BaseFieldTypeLogic {
    constructor(config, 
    // TODO: make required and delete default value
    appRecordsService = types_1.defaultAppRecordsService) {
        var _a, _b, _c, _d, _e, _f;
        this.icon = "";
        this.iconColor = "";
        this.label = "";
        this.placeholder = "";
        this.searchSynonyms = [];
        /** Mode used when displayed in the group header. */
        this.groupByMode = "grid";
        /** Mode used when displayed in a lookup field. */
        this.lookupMode = null;
        this.singleExportEligible = false;
        this.valueToGoogleSheetArray = undefined;
        this.aggregates = {
            empty: {
                label: (0, intl_1.$t)(texts_1.aggregatesText.empty),
                aggregator: exports.countEmpty,
            },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: exports.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: exports.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: exports.percentEmpty,
            },
        };
        this.appLookupKey = "id";
        this.isReference = false;
        this.isDate = false;
        this.isDateRange = false;
        this.isNumber = false;
        this.isCalculated = false;
        this.isFormula = false;
        this.useTargetFieldMigrations = false;
        this.showConfirmationOnMigrate = false;
        // used to handle specific cases for field types that have to be unique (e.g. Autonumber)
        this.uniqueByApplication = false;
        // used to map the comparison method and generator function
        this.comparisons = {};
        // Used to determine where we should display the remove invalid button
        this.removeInvalid = false;
        this.params = {};
        this.sorts = [];
        this.migrations = [];
        // used to map the paste function from clipboard
        this.clipboardPasteRules = {};
        // Use to migrate touched record value when user update field
        this.migrationRules = {};
        this.appRecordsService = appRecordsService;
        this.category = (_a = config.category) !== null && _a !== void 0 ? _a : types_1.defaultChoiceValue;
        this.group = (_b = config.group) !== null && _b !== void 0 ? _b : types_1.defaultChoiceValue;
        this.description = config.description;
        this.params = (_c = config.params) !== null && _c !== void 0 ? _c : {};
        this.sorts = (_d = config.sorts) !== null && _d !== void 0 ? _d : [];
        this.migrations = (_e = config.migrations) !== null && _e !== void 0 ? _e : [];
        this.dateModes = (_f = config.date_modes) !== null && _f !== void 0 ? _f : {};
        this.comparisonsOptions = config.comparisons_options;
        this.sortsOptions = config.sorts_options;
    }
    /**
     * Returns a dict of validator functions
     */
    get validators() {
        return {};
    }
    /**
     * Return an object that is passed down to the filter component
     * specified by the `getFilterComponent` method.
     *
     * @param _field application field
     * @param _comparison comparison name
     */
    getFilterComponentData(_field, _comparison) {
        return { placeholder: this.placeholder };
    }
    /**
     * Converts a raw clipboard value to an internal field value
     *
     * @param clipboardValue extracted clipboard value
     * @param clipboardField extracted field from clipboard
     * @param targetField target application field
     * @param userPreferences user preferences
     * @param currentValue current target field value
     * @param extra a dictionary of optional arguments for advanced use-cases
     */
    parseClipboardFieldValue(clipboardValue, clipboardField, targetField, userPreferences, currentValue, extra, appRecordsService) {
        var _a;
        const clipboardFieldType = (_a = clipboardField === null || clipboardField === void 0 ? void 0 : clipboardField.field_type) !== null && _a !== void 0 ? _a : "unknownfield";
        const pasteFunc = this.getClipboardPasteRules(clipboardFieldType, clipboardField);
        if (!pasteFunc) {
            return currentValue;
        }
        return pasteFunc.call(this, clipboardValue, clipboardField, targetField, userPreferences, currentValue, extra, appRecordsService);
    }
    /**
     * Returns rules of behavior of the component in inline edit mode
     *
     * @param _field         application field
     */
    getInlineEditRules(field) {
        return {
            saveKeys: ["Enter", "Tab"],
            rollbackOnPressEscapeKey: true,
            deleteValueOnFirstKeypress: true,
            closeWhenClickOutside: true,
            enableCopyEventInEditMode: false,
            enablePasteEventInEditMode: false,
            switchToInputControl: "OnSelectedCellClick",
            editOnStartTyping: true,
            editOnDisplayChangeValue: false,
            hideError: false,
            editorWidth: "250px",
            canDeleteValue: true,
            allowEditNestedFields: false,
            inputControlEditMode: "OnShow",
            cellPresenceEventMode: "OnEdit",
            allowAddRecordToGroup: !(0, fields_1.isFieldNested)(field),
            spaceKeyOpensRecordModal: (isEditMode) => !isEditMode,
            needUseDefaultValue: (value, _isDirty) => (0, fields_1.needUseDefaultFieldValue)(field, value),
            canUnselectCell: (value, isDirty, isValid, fieldsMetadata) => {
                if (isValid) {
                    return true;
                }
                return this.getInlineEditRules(field).needUseDefaultValue(value, isDirty);
            },
            isShowValidationError: (errorMessage, value, isDirty) => {
                if (!errorMessage) {
                    return false;
                }
                const useDefaultValue = this.getInlineEditRules(field).needUseDefaultValue(value, isDirty);
                return !useDefaultValue;
            },
            canBulkPaste: (inlineEditRules, isEditMode) => {
                if (isEditMode && inlineEditRules.enablePasteEventInEditMode) {
                    return true;
                }
                return !isEditMode;
            },
            canBulkClean: (field) => {
                if ((0, fields_1.isFieldNested)(field) && !(0, fields_1.isEditingAllowed)(field)) {
                    return false;
                }
                const supportBulkPaste = !!this.getClipboardPasteRules(field.field_type);
                return !(0, fields_1.isFieldRequired)(field) && supportBulkPaste;
            },
        };
    }
    /**
     * Returns rules of behavior of the field in view controls
     *
     * @param _field         application field
     */
    getViewControlRules(_field) {
        return (0, view_control_rules_1.getDefaultViewControlRules)();
    }
    /**
     * Converts a field value to a key for sorting.
     * Different fields require different transform process. Used in generic sorting function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   current user preferences
     * @param record            application record
     * @param _structure        application structure
     * @param _rule
     */
    sortKey(fieldValue, field, userPreferences, record, _structure, _rule) {
        return this.valueToString(fieldValue, field, userPreferences, record);
    }
    /**
     * Converts a field value to a key for sorting groups.
     * Different fields require different transform process. Used in generic sorting function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   current user preferences
     * @param record            application record
     * @param structure         application structure
     * @param rule
     */
    groupBySortKey(fieldValue, field, userPreferences, record, structure, rule) {
        return this.sortKey(fieldValue, field, userPreferences, record, structure, rule);
    }
    /**
     * Determines that field should cache result of sortKey or groupBySortKey
     * function
     *
     * Some field types have complex logic to get safe value and sortKey or
     * groupBySortKey function, and it's better to cache the result for performance
     * reasons.
     *
     * used in the sortRecords function.
     *
     * @param field         application field
     *
     */
    useSortCache(_field) {
        return true;
    }
    /**
     * Determines that field using natural sorting logic
     *
     * @param field         application field
     * @param fieldValue    field value
     * @param isGroupByRule true if rule is group by rule
     */
    useNaturalSorting(field, fieldValue, isGroupByRule) {
        return true;
    }
    /**
     * Return field sort options
     *
     * @param _field application field
     */
    getSortsOptions(_field) {
        return this.sortsOptions;
    }
    /**
     * Determine field to sort in grid column header
     * In some cases we need to sort by first nested field
     *
     * @param field   application field
     */
    sortField(field) {
        return field;
    }
    /**
     * Return sort field slug
     *
     * @param field   application field
     */
    sortFieldSlug(field) {
        return (0, fields_1.getFieldSlug)(this.sortField(field));
    }
    /**
     * Converts a field value to a key for grouping.
     * Different fields require different transform process. Used in generic grouping function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   current user preferences
     * @param record            record data
     * @param _structure         application structure
     */
    groupByKey(fieldValue, field, userPreferences, record, _structure) {
        return this.valueToString(fieldValue, field, userPreferences, record).toLowerCase();
    }
    /**
     * Converts a field value to a object with keys and values for grouping.
     * Different fields require different transform process.
     * Must always return a matrix.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param _field             application field
     * @param _record            record data
     * @param userPreferences
     */
    getGroupValues(fieldValue, _field, _record, _userPreferences) {
        return [fieldValue];
    }
    /**
     * Determine field to filter in grid column header
     * In some cases we need to filter by first nested field
     *
     * @param field   application field
     */
    groupByField(field) {
        return field;
    }
    /**
     * Return grupBy field slug
     *
     * @param field   application field
     */
    groupByFieldSlug(field) {
        return (0, fields_1.getFieldSlug)(this.groupByField(field));
    }
    /**
     * Converts a field value to a key for searching.
     * Different fields require different transform process. Used in the generic search function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   current user preferences
     * @param record
     */
    searchKey(fieldValue, field, userPreferences, record) {
        return this.valueToString(fieldValue, field, userPreferences, record);
    }
    /**
     * Converts a field value to a value for exporting.
     * Different fields require different transform process. Used in generic exporting function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   preferences
     * @param _record           record
     */
    valueToExport(fieldValue, field, userPreferences, _record) {
        return this.valueToString(fieldValue, field, userPreferences, _record);
    }
    /**
     * Converts a field value to a value for exporting to Excel files.
     * Different fields require different transform process. Used in generic exporting function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   preferences
     * @param _record           record
     */
    valueToExcelExport(fieldValue, field, userPreferences, _record) {
        return {
            t: "s",
            v: this.valueToExport(fieldValue, field, userPreferences, _record),
        };
    }
    /**
     * Converts a nested field label to export header.
     * Different fields require different transform process. Used in xlsx and csv export.
     *
     * @param label             label of current nested field
     * @param field             parent application field
     */
    getNestedExportHeader(label, field) {
        return label;
    }
    /**
     * Converts a field value to a value for exporting to google sheet.
     * Different fields require different transform process. Used in generic exporting function.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   preferences
     */
    valueToGoogleSheet(fieldValue, field, userPreferences, _record) {
        return (0, google_sheet_1.makeRowDataValues)(this.valueToString(fieldValue, field, userPreferences), "stringValue");
    }
    /**
     * Represents a field value as string.
     * Different fields has different value. It should convert any value to a string.
     * For example, it is used in generic sorting and grouping functions.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param _userPreferences   current user preferences
     * @param _record            application record
     */
    valueToString(fieldValue, field, _userPreferences, _record) {
        const { params: { choices }, } = field;
        if (fieldValue instanceof Array) {
            const values = choices
                ? (0, at_1.default)((0, keyBy_1.default)(choices, "value"), fieldValue).map((choice) => choice === null || choice === void 0 ? void 0 : choice.label)
                : fieldValue;
            return values.join(", ");
        }
        else if (choices) {
            return (0, fields_1.getFieldChoiceLabel)(field, fieldValue);
        }
        else if (Boolean(fieldValue) &&
            fieldValue instanceof Object &&
            "sys_root" in fieldValue) {
            return fieldValue.sys_root || "";
        }
        return fieldValue || "";
    }
    /**
     * Represent filterValue as string.
     * Different filters can provide different values and sometimes its not same type as field value.
     * For example, IPAddressField has value type of IpAddress[] but filter value has type of string[]
     *
     * @param filterValue       value that gotten by filter
     * @param field             application field
     * @param userPreferences   preferences
     * @param _comparison       comparison
     */
    filterValueToString(filterValue, field, userPreferences, _comparison) {
        return this.valueToString(filterValue, field, userPreferences);
    }
    /**
     * Gives human-friendly string representation of a filter value.
     *
     * Generally we represent it using DisplayFieldValue, but there may be exceptions
     * such as social network field, with its special field structure.
     *
     * @param filterValue       value that gotten by filter
     * @param field             application field
     * @param userPreferences   preferences
     */
    fieldParamsFilterValueToDisplay(filterValue, _field, _userPreferences) {
        var _a, _b;
        return ((_b = (_a = filterValue === null || filterValue === void 0 ? void 0 : filterValue.label) !== null && _a !== void 0 ? _a : filterValue === null || filterValue === void 0 ? void 0 : filterValue.value) !== null && _b !== void 0 ? _b : filterValue);
    }
    /**
     * Represents a field value as string in Metrics dashboards widgets.
     * Different fields has different value. It should convert any value to a string..
     * For example comparison widget with number field should with fixed precision
     *
     * @param fieldValue        field value is gotten by field slug
     * @param _field             application field
     * @param userPreferences   current user preferences
     */
    metricWidgetValueToString(fieldValue, _field, userPreferences) {
        var _a, _b, _c, _d, _e, _f;
        return (0, detailed_decimal_1.convertNumberToFormatedString)({
            value: Number(fieldValue),
            precision: (_b = (_a = _field.params) === null || _a === void 0 ? void 0 : _a.precision) !== null && _b !== void 0 ? _b : (0, number_1.getNumberPrecision)(Number(fieldValue)),
            decimalSeparator: (0, number_1.getDecimalSeparator)((_d = (_c = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : "en-US"),
            thousandSeparator: (0, number_1.getGroupSeparator)((_f = (_e = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : "en-US"),
        });
    }
    /**
     * Represents a field value as string in Progress dashboards widget.
     * Different fields has different value. It should convert any value to a string..
     * For example goal of Progress widget with number that included precisions
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   current user preferences
     */
    progressWidgetGoalValueToString(fieldValue, field, userPreferences) {
        return this.metricWidgetValueToString(fieldValue, field, userPreferences);
    }
    /**
     * Determine field to filter in grid column header
     * In some cases we need to filter by first nested field
     *
     * @param field   application field
     */
    filterField(field) {
        return field;
    }
    /**
     * Get the available comparisons for the field.
     * Used to dynamically pick comparisons for lookup and formula.
     *
     * @param _field   application field
     * @param _rule    specified filtering rule
     */
    getComparisons(_field, _rule) {
        return this.comparisons;
    }
    /**
     * Return Comparison options for the given field.
     *
     * @param _field the application field
     */
    getComparisonsOptions(_field) {
        return this.comparisonsOptions;
    }
    /**
     * Return an array with valid values only.
     *
     * @param values array of values
     */
    removeInvalidValues(values) {
        return values;
    }
    /**
     * Returns the size of the contents of the field value.
     * Different fields have different meanings. It should convert any value to a string and
     * get the width of the content occupied in the browser.
     * Used to calculate the automatic width of a column in a table.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param userPreferences   current user preferences
     * @param record            application record
     * @param font         specify font size and name (e.g. 'bold, 10px arial')
     */
    getContentSize(fieldValue, field, userPreferences, record, font) {
        const strValue = this.valueToString(fieldValue, field, userPreferences, record);
        return (0, dom_1.getTextSize)(strValue, undefined, font);
    }
    /**
     * Handles any special cases for migrating from one field type to another.
     *
     * @param fieldTo    new field structure
     * @param fieldFrom  old field structure
     * @param applicationId   application ID
     * @returns          the updated new field structure
     */
    migrateField(fieldTo, fieldFrom, applicationId) {
        return (0, migrations_1.migrateBaseField)(fieldTo, fieldFrom, applicationId);
    }
    // Filtration  ------------------------------------------------------------------------------
    /**
     * Converts a filter rule to a predicate function that uses it. Returns a predicate function that
     * allows you to cache various modifications by field value and filter value.
     * It optimizes search and filtering processes.
     *
     * @param rule              specified filtering rule
     * @param field
     * @param userPreferences
     * @param _structure
     */
    makeRulePredicate(rule, field, userPreferences, _structure) {
        var _a;
        const { value: filterValue, field: slug, comparison } = rule;
        if (comparison === "never") {
            return () => false;
        }
        const generator = this.getComparisons(field, rule)[comparison];
        if (typeof generator !== "function") {
            // eslint-disable-next-line no-console
            console.warn(`Predict generator for comparison method "${comparison}" doesn't exist.
        You should specify generator for the ${(_a = this.name) !== null && _a !== void 0 ? _a : ""} TypeField class.`);
            return () => false;
        }
        // get compare function from predicate generator function
        const comparer = generator(this.processFilterValue(filterValue, comparison, field, userPreferences));
        const fieldForFiltering = this.getFieldAppTargetStructure(field);
        // generate a function for filtering
        return (record) => {
            const value = this.processFieldValueForFiltering(record, slug, comparison, field, userPreferences);
            return comparer(value, fieldForFiltering, userPreferences);
        };
    }
    /**
     * Used to apply the necessary transformations for a filter value.
     * Different field type requires different transform process. It should return the converted
     * filter value. It is used for building predicate function
     *
     * @param filterValue        field value is gotten by field slug
     * @param _comparison        type of comparison
     * @param _field             field with params
     * @param _userPreferences   User preferences such as timezone, locale etc.
     */
    processFilterValue(filterValue, _comparison, _field, _userPreferences) {
        return filterValue;
    }
    /**
     * Determines whether the current field is inserted before a primary field
     * when the field order is changed in a list
     */
    canMoveFieldBeforePrimaryField() {
        return false;
    }
    /**
     * Converts a value which is used for filtering to a value that can be stored in a record.
     */
    filterToRecordValue(filterValue, comparison, field, userPreferences, _recordValue) {
        return this.processFilterValue(filterValue, comparison, field, userPreferences);
    }
    /**
     * Returns date filters for calendar view.
     */
    getCalendarFilters(fieldSlug, startPeriod, endPeriod) {
        return {
            field: fieldSlug,
            comparison: "is",
            value: {
                date_mode: "date_range",
                date_mode_value: {
                    lower: startPeriod,
                    upper: endPeriod,
                    bounds: "[]",
                },
            },
        };
    }
    /**
     * User to past field value to calendar event
     *
     * @param event           Calendar Event
     * @param _fieldValue      field value
     * @param _userPreferences user preferences
     * @param _field           app field
     */
    processCalendarValue(event, _fieldValue, _userPreferences, _field) {
        return event;
    }
    /**
     * In some view modes, group values are displayed in text form.
     * This method converts the field value to a text representation for display in the group.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param field             application field
     * @param [userPreferences]
     * @param [record]
     */
    getGroupLabel(fieldValue, field, userPreferences, record) {
        return this.valueToString(fieldValue, field, userPreferences, record);
    }
    /**
     * Used to create field value from date string. It's dates specific method.
     *
     * @param _date
     * @param _includeTime
     */
    getDateValueFromString(_date, _includeTime) {
        return null;
    }
    /**
     * Used to add moment.Duration to field value.
     */
    addDuration(_fieldValue, _duration, _userPreferences, _includeTime) {
        return null;
    }
    /**
     * Prepare term before search.
     */
    prepareSearchTerm(searchTerm) {
        return searchTerm.trim().toLowerCase();
    }
    /**
     * Used to get the search term and apply the necessary transformations for search.
     * Different field type requires different transform process.
     * We allow specifying multiple search values separated by ;,' and space.
     */
    processSearch(searchTerm, fieldValue, field, userPreferences, record) {
        const preparedTerm = this.prepareSearchTerm(searchTerm);
        if (!preparedTerm) {
            return false;
        }
        const preparedFieldValue = this.searchKey(fieldValue, field, userPreferences, record);
        return preparedFieldValue.toLowerCase().includes(preparedTerm);
    }
    /**
     * Used to get the field value and apply the necessary transformations.
     * Different field type requires different transform process. It should return the converted
     * field value. It is used for building predicate function
     *
     * @param record              instance of a record
     * @param slug                field slug
     * @param field
     * @param _comparison         type of comparison
     * @param _userPreferences
     */
    processFieldValue(record, slug, _comparison, field, _userPreferences) {
        return this.getFieldValue(field, record);
    }
    processFieldValueForFiltering(record, slug, comparison, field, userPreferences) {
        return this.processFieldValue(record, slug, comparison, field, userPreferences);
    }
    /**
     * Returns a representation of a blank value for the current field.
     */
    getEmptyValue() {
        return null;
    }
    /**
     * Checks necessity to clean up empty values from records list before filtering.
     * */
    shouldCleanUpEmptyInLookupFilter(comparison) {
        return true;
    }
    /**
     * Gets value from record by field using safely retrieving
     *
     * If a field value is invalid, an empty value is used
     */
    getFieldValue(field, record) {
        try {
            if (field.parent) {
                const parentFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(field.parent.field_type);
                const parentFieldValue = parentFieldLogic.fieldValueGetter.getNestedFieldValue(field.parent, field.slug, record);
                return parentFieldValue !== null && parentFieldValue !== void 0 ? parentFieldValue : this.fieldValueGetter.getEmptyValue();
            }
            return this.fieldValueGetter.getFieldValue(field, record);
        }
        catch (err) {
            if ((0, field_logic_logging_1.isNotErrorSent)(record === null || record === void 0 ? void 0 : record.application_id, field.field_type)) {
                (0, core_1.captureException)(err, (scope) => {
                    scope.setExtra("message", "Incorrect field value detected");
                    scope.setExtra("field", field);
                    scope.setExtra("record", record);
                    return scope;
                });
                (0, field_logic_logging_1.setErrorInCache)(record === null || record === void 0 ? void 0 : record.application_id, field.field_type);
            }
            return this.fieldValueGetter.getEmptyValue();
        }
    }
    /**
     * Used to get the initial field value for a grid cell in case when a cell is focused and
     * a keyboard key is typed.
     * In this case a grid cell goes to Edit mode with transformed initial field value.
     * Different field type requires different transform process.
     *
     * @param _startValue       A typed key symbol
     * @param _field            Application field
     * @param _userPreferences  User preferences
     */
    getInitialFieldValue(_startValue, _field, _userPreferences) {
        return this.getEmptyValue();
    }
    /**
     * Returns a default value for the field.
     *
     * Most fields store the same type as their own value type.
     * Some fields can store a default value as text. For example,
     * Due Date field can store value like a "Today+2".
     * AssignTo field can store default value like a "Current"
     */
    getDefaultValue(field, _userPreferences) {
        return field.params.default_value;
    }
    /**
     * Checks if the values are equal.
     *
     * @param fieldValue1        field value is gotten by field slug
     * @param fieldValue2        field value is gotten by field slug
     * @param field
     * @param userPreferences
     */
    isValueEqual(fieldValue1, fieldValue2, field, userPreferences) {
        return (this.valueToString(fieldValue1, field, userPreferences) ===
            this.valueToString(fieldValue2, field, userPreferences));
    }
    /**
     * Checks if the value is equal to the original empty field value.
     *
     * @param fieldValue        field value is gotten by field slug
     * @param _field
     */
    isValueEmpty(fieldValue, _field) {
        return (!fieldValue ||
            (0, react_fast_compare_1.default)(fieldValue, this.getEmptyValue()) ||
            ((0, isPlainObject_1.default)(fieldValue) && (0, react_fast_compare_1.default)(this.getEmptyValue(), fieldValue)));
    }
    /**
     * Checks if the default value is actually empty.
     */
    isDefaultEmpty(field) {
        return this.isValueEmpty(this.getDefaultValue(field), field);
    }
    getFieldValueFromRecord(record, field) {
        return this.getFieldValue(field, record);
    }
    // Copy action  ------------------------------------------------------------------------------
    /**
     * Prepares field data to be copied to clipboard.
     *
     * @param {AppField} field
     */
    prepareFieldDataToClipboard(field) {
        return { field_type: field.field_type, params: field.params };
    }
    /**
     * The main method for copying field values to the clipboard.
     * Gets values in text and raw value, then puts everything in the clipboard
     *
     * @param clipboard         clipboard object
     * @param fieldValue        field value is gotten by field slug
     * @param field             source application field
     * @param userPreferences   user preferences
     * @param record
     */
    putValueToClipboard(clipboard, fieldValue, field, userPreferences, record) {
        const { text, rawValue } = this.prepareValueForClipboard(fieldValue, field, userPreferences, record);
        if (clipboard) {
            clipboard.setData("text/plain", text);
            const jsonValue = {
                field: this.prepareFieldDataToClipboard(field),
                value: rawValue,
            };
            clipboard.setData("application/json", JSON.stringify(jsonValue));
        }
        else {
            (0, clipboard_1.copyTextToClipboard)(text);
        }
    }
    /**
     * Returns a representation of a value that can be copied to the clipboard.
     * It should return text and raw value (optional) format
     *
     * @param value             field value is gotten by field slug
     * @param field             source application field
     * @param userPreferences   user preferences
     * @param _record            database record where value stored
     */
    prepareValueForClipboard(value, field, userPreferences, _record) {
        return {
            text: this.valueToString(value, field, userPreferences),
            rawValue: value,
        };
    }
    // Validation  ------------------------------------------------------------------------------
    /**
     * Collects all determined synchronous validators for the field
     */
    getFormValidators(field, applicationId, records, record) {
        // concatenate validators
        const validatorsDict = this.validators;
        return Object.keys(validatorsDict).reduce((validators, key) => {
            var _a;
            const validatorCreator = validatorsDict[key];
            if (validatorCreator) {
                const validator = (_a = validatorCreator(field, records, record, applicationId)) === null || _a === void 0 ? void 0 : _a.validator;
                if (validator && validator !== validation_1.dummyValidator) {
                    validators.push(validator);
                }
            }
            return validators;
        }, []);
    }
    /**
     * Collects error messages for parameters of field for which validators are defined
     *
     * @param field   application field
     */
    getErrorMessages(field, records, record) {
        let messages = {};
        // concatenate sync error messages
        const validatorsDict = this.validators;
        messages = Object.keys(validatorsDict).reduce((allMessages, key) => {
            const validatorCreator = validatorsDict[key];
            if (validatorCreator) {
                allMessages = {
                    ...allMessages,
                    [key]: validatorCreator(field, records, record).message,
                };
            }
            return allMessages;
        }, messages);
        return messages;
    }
    // Paste action  ------------------------------------------------------------------------------
    /**
     * Converts a value from a clipboard to an internal field value
     *
     * @param clipboard         clipboard object
     * @param targetField       target application field
     * @param userPreferences   user preferences
     * @param currentValue      current target field value
     */
    getValueFromClipboard(clipboard, targetField, userPreferences, currentValue, extra) {
        const clipboardText = clipboard.getData("text");
        let clipboardJson = { field: {}, value: undefined };
        try {
            clipboardJson = JSON.parse(clipboard.getData("application/json"));
        }
        catch (e) { }
        const clipboardValue = {
            text: clipboardText,
            rawValue: clipboardJson.value,
        };
        return this.parseClipboardFieldValue(clipboardValue, clipboardJson.field, targetField, userPreferences, currentValue, extra);
    }
    /**
     * Returns field cope/paste rule function
     *
     * @param fieldType    field type in the clipboard
     * @param clipboardField  clipboard field instance (optional)
     */
    getClipboardPasteRules(fieldType, clipboardField) {
        if ((clipboardField === null || clipboardField === void 0 ? void 0 : clipboardField.params) &&
            !!(0, fields_1.getFieldParamValue)(clipboardField, "target_field_structure")) {
            return (0, target_field_1.withTargetField)(clipboardField, undefined, (targetField) => {
                return this.clipboardPasteRules[targetField.field_type];
            });
        }
        return this.clipboardPasteRules[fieldType];
    }
    /**
     * Returns additional css class for field according field params
     *
     * @param _field                    application field
     */
    getCssClass(_field) {
        return "";
    }
    /**
     * Specify whether the application should reload after this field parameters is
     * changed.
     */
    reloadOnMigrate(field, filters) {
        return false;
    }
    /**
     * If this returns an error message, the field form should show an error
     * instead of allowing the user to edit the field params.
     */
    hasUneditableFieldError(_field, _application) {
        return false;
    }
    /**
     * Specify whether the field can be deleted
     *
     * @param field                    application field
     */
    canDelete(field) {
        const isDefaultSystem = types_1.defaultSystemFieldsSlugs.includes(field.slug);
        return !isDefaultSystem && !(0, fields_1.isFieldPrimary)(field) && !(0, fields_1.isFieldNested)(field);
    }
    /**
     * Specify whether the field settings can be modified
     *
     * @param field                    application field
     */
    canModifySettings(field) {
        if ((0, fields_1.isFieldNested)(field)) {
            return false;
        }
        return !(0, fields_1.isFieldSystem)(field) || this.isCalculated;
    }
    /**
     * Specify whether the field should show rollback icon controller
     *
     * @param _field                    application field
     */
    showRollback(_field) {
        return true;
    }
    /**
     * Extracts a value from field.params.choice to set the value to the record.
     */
    getRecordValueFromFieldChoice(fieldChoice) {
        return fieldChoice;
    }
    /**
     * Specify whether the field should show a lock icon
     *
     * @param field                     application field
     * @param _view                     view, where icon should be shown or not
     * @param _application              the application model
     * @param _isSolutionOwner          current member has solution owner access
     */
    showSystemLockIcon(field, _view, _application, _memberAccess) {
        var _a;
        return (_a = field.params.system) !== null && _a !== void 0 ? _a : !!field.parent;
    }
    /**
     * Return the nested fields structure of the current field type.
     *
     * @param field the application field
     */
    getNestedFields(field) {
        var _a, _b;
        return (_b = (_a = field.nested) !== null && _a !== void 0 ? _a : field.params.nested) !== null && _b !== void 0 ? _b : [];
    }
    /**
     * Return the nested formula fields schema that is used by formula builder.
     * (Formula schema contains minimal apps and fields data that is required for formula builder)
     *
     * @param fieldSchema formula field schema
     */
    getNestedFormulaFieldsSchema(fieldSchema) {
        return fieldSchema.nested;
    }
    /**
     * Specify whether a field value can be changed from one specific value to another
     */
    canChangeValue(field, _fromValue, toValue) {
        return (!(0, fields_1.isFieldRequired)(field) ||
            ((0, fields_1.isFieldRequired)(field) && !this.isValueEmpty(toValue, field)));
    }
    canHaveReferencedRecords() {
        return false;
    }
    getReferencedRecords(_fieldValue, _field, _record, _structure) {
        return [];
    }
    getReferencedRecordsIds(fieldValue, field, record, structure) {
        return this.getReferencedRecords(fieldValue, field, record, structure).map((request) => request.recordId);
    }
    /**
     * Formats activity record change values: previous field value and current field value.
     * For example, for multiple fields, we should only show added/removed elements.
     */
    formatActivityRecordChangeValues(previousValue, currentValue, _extraData, _field) {
        return { previousValue, currentValue };
    }
    /**
     * Returns initial params for the field.
     */
    getInitialParams(_field, _structure) {
        return {};
    }
    /**
     * Returns field params that are usable for any field type.
     * The common use-case for this is to clear field-specific parameters, such as
     * system, primary etc.
     *
     * @param field  field instance for which params will be generated.
     * @returns      filled field params.
     */
    getCommonFieldParams(field) {
        return { ...field.params, required: false };
    }
    /**
     * Handle logic to generate field slug based on field type.
     * Unique fields, like autonumber, are always present in the app structure,
     * but set as hidden until the user selects it.
     * So, instead of adding a new field to application, it only sets the hidden flag to false,
     * keeping the same slug.
     *
     * @returns generated field slug
     */
    getAppFieldSlug() {
        return (0, fields_1.generateSlug)();
    }
    /**
     * Returns grid column settings for a particular field
     */
    // TODO: ask Misha
    getGridColumnSettings(_field) {
        return {
            icon: this.icon,
            showLabel: true,
            defaultWidth: 200,
            minWidth: 70,
            resizable: true,
            contentAlignment: "left",
            headerAlignment: "centered",
        };
    }
    /**
     * Determines whether this field allow expansion inside record modal.
     */
    isFieldExpandableOnRecordModal(_field) {
        return false;
    }
    /**
     * Return target structure for a particular field
     * */
    getFieldAppTargetStructure(field) {
        return field;
    }
    /**
     * Return field field_type
     * */
    getFieldType(field) {
        return field.field_type;
    }
    /**
     * Checks if this field type has enabled group highlighting.
     */
    isGroupHighlighted(_field) {
        return false;
    }
    /**
     * Retrieves field choice that is used in presented record.
     *
     * @param _record - A wrapped record. Since multiple values are possible, here we work with wrapped record.
     * @param _field - A field that contains needed choices.
     * @returns Field choice or null.
     */
    getChoiceByRecordValue(_record, _field) {
        return null;
    }
    getChoiceColor(field, choice) {
        return choice.value_color;
    }
    /**
     * Determines whether load full value for the field is necessary
     */
    isFullValueLoadNeeded(_fieldValue) {
        return false;
    }
    /**
     * Determines whether filed value is partial
     */
    isPartialValue(_fieldValue) {
        return false;
    }
    /**
     * Determines whether show field activity updates for this value or not
     */
    showActivityUpdate(fieldValue, field) {
        return !this.isValueEmpty(fieldValue, field);
    }
    /**
     * Determines that imported value is correct or not
     */
    isImportedValueValid(parsedValue, dataValue) {
        return !(this.isValueEmpty(parsedValue, undefined) && dataValue);
    }
    /**
     * Allows to replace the value in the record for specified field.
     *
     * Used for a drag-and-drop feature to replace a value when moving a record from one group
     * to another. We need to change the values so that the record must get into the specified group.
     *
     * @example
     * record: { text: 'Some text' }
     * sourceValue: 'Some text'
     * targetValue: 'Other text'
     * Result: 'Other text'
     */
    replaceValue(field, record, sourceValue, targetValue, _userPreferences) {
        return targetValue !== null && targetValue !== void 0 ? targetValue : this.getEmptyValue();
    }
    /**
     * Returns options for Duplicate Field Modal
     */
    getDuplicateFieldOptions(field) {
        return {
            includeContentChecked: true,
            includeContentDisabled: field.params.system,
        };
    }
    getAggregateDisplayValue(fieldValue, field, userPreferences, record) {
        return this.valueToString(fieldValue, field, userPreferences, record);
    }
    /**
     * Gets dimensions of a field to determine how the field should be displayed in LinkedRecordField.
     *
     * When LinkedRecordField has the Expanded display format, we should show as many fields as fit
     * to the modal window.
     */
    getLinkedRecordFieldSmartWidth(_field) {
        return { defaultWidht: 0, minWidth: 0 };
    }
    /**
     * Returns a value to be used in Record Title field's auto generated value.
     */
    getRecordTitleVariableValue(variableName, fieldValue, field, userPreferences, record) {
        return this.valueToString(fieldValue, field, userPreferences, record);
    }
    /**
     * Returns a function for updating record value when field migrating.
     */
    getMigrationFunction(targetFieldTypeName) {
        return this.migrationRules[targetFieldTypeName];
    }
    /**
     * Returns options which can be used as a varibles in Record Title field's auto generated value.
     */
    getRecordTitleVariableOptions(field) {
        return [{ title: field.label, value: field.slug, icon: this.icon }];
    }
    /**
     * Prepares field to be send to the server.
     *
     * Removes all unnecessary properties.
     */
    serializeField(field) {
        return (0, omit_1.default)(field, [
            "nested",
            "parent",
            "icon",
            "disabled",
            "placeholder",
            "pattern",
        ]);
    }
    getEditRecordInputMenuActions(_field, _memberAccess) {
        return [];
    }
    /**
     * Determines that the field is supported for the calendar
     */
    isSupportedCalendarField(field) {
        var _a;
        const isValidField = (_a = field.params.valid) !== null && _a !== void 0 ? _a : true;
        return isValidField && this.isDate;
    }
    /**
     * Determines whether the field supports bulk action
     */
    isSupportedByBulkAction(field) {
        return !field.params.unique;
    }
    /**
     * Gets empty value for FilterField.value
     */
    getEmptyFilterValue(_field, _comparison) {
        return null;
    }
    /**
     * Returns default FilterField to be used in FilterFindow
     * when adding new filter row with specific field.
     */
    getDefaultFilterField(field) {
        const comparisons = this.getComparisonsOptions(field);
        const defaultComparison = comparisons[0].value;
        return {
            comparison: defaultComparison,
            field: (0, fields_1.getFieldSlug)(field),
            value: this.getEmptyFilterValue(field, defaultComparison),
        };
    }
    getFillPattern(_values, _field, _reverse) {
        return null;
    }
    copyValue(value) {
        return value;
    }
    /**
     * Returns a function that receives field's value and return color
     */
    getSpotlightColorizer(_field) {
        return null;
    }
    /**
     * Returns array of field's color choices.
     */
    getSpotlightColorChoices(_field) {
        return [];
    }
    /**
     * Returns a function that receive a field value and returns RecordRequest[]
     * in case if the field can't contain related records returns the null
     */
    getRelatedRecordRequestsGetter(_field) {
        return null;
    }
    /**
     * Applies a bulk update to a field value.
     */
    applyBulkUpdate(field, value, update) {
        return update.newValue;
    }
    /**
     * Retrieves the metadata of a specified field from a given record.
     *
     * @param field - The field object for which metadata is to be retrieved.
     * @param record - The record containing field metadata.
     * @param fieldsMetadata - The record containing field metadata.
     *
     * @return {unknown} The metadata associated with the specified field, or null if not found.
     */
    getFieldMetadata(field, record, fieldsMetadata) {
        var _a, _b, _c;
        if (fieldsMetadata) {
            return (_a = fieldsMetadata === null || fieldsMetadata === void 0 ? void 0 : fieldsMetadata[field.slug]) !== null && _a !== void 0 ? _a : null;
        }
        return ((_c = (_b = record === null || record === void 0 ? void 0 : record[types_1.FIELDS_METADATA_SLUG]) === null || _b === void 0 ? void 0 : _b[field.slug]) !== null && _c !== void 0 ? _c : null);
    }
    /**
     * Compare record field metadata
     *
     * @param value1
     * @param value2
     */
    isFieldMetadataEqual(value1, value2) {
        return (!value1 && !value2) || (0, react_fast_compare_1.default)(value1, value2);
    }
    /**
     * Validates the metadata of a field based on the provided value.
     *
     * @param newValue
     */
    isFieldMetadataValid(newValue) {
        return true;
    }
    /**
     * Checks if the search term matches the field's metadata
     *
     * @param searchTerm The term to search for
     * @param field The field being searched
     * @param record The record containing the field
     * @returns True if the search term is found in the field's metadata
     */
    processSearchInFieldMetadata(searchTerm, field, record) {
        return false;
    }
}
exports.BaseFieldTypeLogic = BaseFieldTypeLogic;
